import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, FileEdit } from "lucide-react";
import { toast } from "sonner";

const EMPTY_MATERIAL = { title: "", body: "", order: 1, published: true, image_url: "", video_url: "", pdf_url: "" };
const EMPTY_SCENARIO = {
  title: "", objective: "", instructions: "", challenges: "", difficulty: "Mudah", published: true,
  questions: [],
  preset_config: { column_id: "col-c18", organic_solvent_id: "sol-acn", percent_organic: 30, buffer_ph: 3.0, flow_rate_ml_min: 1.0, temperature_c: 30, wavelength_nm: 254, runtime_min: 10, injection_volume_ul: 10, elution_mode: "isocratic", noise_level: 0.1, samples: [{ analyte_id: "an-caffeine", concentration_ppm: 50 }] },
  target: { required_analyte_ids: ["an-caffeine"], min_resolution: 1.5, max_runtime_min: 10 },
};

export default function LecturerContent() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
          <FileEdit className="h-7 w-7 text-primary" /> Kelola Konten
        </h1>
        <p className="text-muted-foreground mt-1">Buat dan atur materi pembelajaran serta skenario praktikum.</p>
      </div>
      <Tabs defaultValue="materials">
        <TabsList>
          <TabsTrigger value="materials" data-testid="tab-materials">Materi</TabsTrigger>
          <TabsTrigger value="scenarios" data-testid="tab-scenarios">Skenario</TabsTrigger>
        </TabsList>
        <TabsContent value="materials" className="mt-4"><MaterialsCMS /></TabsContent>
        <TabsContent value="scenarios" className="mt-4"><ScenariosCMS /></TabsContent>
      </Tabs>
    </div>
  );
}

function MaterialsCMS() {
  const qc = useQueryClient();
  const { data: list = [] } = useQuery({ queryKey: ["cms-materials"], queryFn: () => api.get("/materials").then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MATERIAL);

  const openNew = () => { setEditing("new"); setForm(EMPTY_MATERIAL); };
  const openEdit = (m) => { setEditing(m.id); setForm({ ...m }); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    try {
      if (editing === "new") await api.post("/materials", form);
      else await api.put(`/materials/${editing}`, form);
      toast.success("Materi disimpan");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["cms-materials"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus materi ini?")) return;
    await api.delete(`/materials/${id}`);
    qc.invalidateQueries({ queryKey: ["cms-materials"] });
    toast.success("Materi dihapus");
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openNew} className="gap-2" data-testid="new-material-button"><Plus className="h-4 w-4" /> Materi Baru</Button></div>
      <Card className="rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead className="w-16">Bab</TableHead><TableHead>Judul</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((m) => (
              <TableRow key={m.id} data-testid={`cms-material-${m.id}`}>
                <TableCell className="font-data">{m.order}</TableCell>
                <TableCell className="font-medium">{m.title}</TableCell>
                <TableCell><Badge variant={m.published ? "default" : "outline"}>{m.published ? "Publik" : "Draf"}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`edit-material-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Materi Baru" : "Edit Materi"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-3 space-y-1"><Label className="text-xs">Judul</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="material-title-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Urutan</Label><Input type="number" value={form.order} onChange={(e) => upd("order", parseInt(e.target.value) || 1)} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Isi</Label><Textarea rows={6} value={form.body} onChange={(e) => upd("body", e.target.value)} data-testid="material-body-input" /></div>
            <div className="space-y-1"><Label className="text-xs">URL Gambar</Label><Input value={form.image_url} onChange={(e) => upd("image_url", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">URL Video</Label><Input value={form.video_url} onChange={(e) => upd("video_url", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">URL PDF</Label><Input value={form.pdf_url} onChange={(e) => upd("pdf_url", e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => upd("published", v)} /><Label className="text-xs">Publikasikan</Label></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-material-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScenariosCMS() {
  const qc = useQueryClient();
  const { data: list = [] } = useQuery({ queryKey: ["cms-scenarios"], queryFn: () => api.get("/scenarios").then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SCENARIO);
  const [presetText, setPresetText] = useState("");
  const [targetText, setTargetText] = useState("");

  const openNew = () => {
    setEditing("new"); setForm(EMPTY_SCENARIO);
    setPresetText(JSON.stringify(EMPTY_SCENARIO.preset_config, null, 2));
    setTargetText(JSON.stringify(EMPTY_SCENARIO.target, null, 2));
  };
  const openEdit = (s) => {
    setEditing(s.id); setForm({ ...s, questions: s.questions || [] });
    setPresetText(JSON.stringify(s.preset_config || {}, null, 2));
    setTargetText(JSON.stringify(s.target || {}, null, 2));
  };

  const save = async () => {
    let preset, target;
    try { preset = JSON.parse(presetText); } catch { toast.error("JSON Parameter Awal tidak valid"); return; }
    try { target = JSON.parse(targetText); } catch { toast.error("JSON Target tidak valid"); return; }
    const payload = { ...form, preset_config: preset, target, questions: Array.isArray(form.questions) ? form.questions : String(form.questions).split("\n").filter(Boolean) };
    try {
      if (editing === "new") await api.post("/scenarios", payload);
      else await api.put(`/scenarios/${editing}`, payload);
      toast.success("Skenario disimpan");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["cms-scenarios"] });
      qc.invalidateQueries({ queryKey: ["scenarios"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Hapus skenario?")) return; await api.delete(`/scenarios/${id}`); qc.invalidateQueries({ queryKey: ["cms-scenarios"] }); toast.success("Dihapus"); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openNew} className="gap-2" data-testid="new-scenario-button"><Plus className="h-4 w-4" /> Skenario Baru</Button></div>
      <Card className="rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Judul</TableHead><TableHead>Tingkat</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id} data-testid={`cms-scenario-${s.id}`}>
                <TableCell className="font-medium">{s.title}</TableCell>
                <TableCell>{s.difficulty}</TableCell>
                <TableCell><Badge variant={s.published ? "default" : "outline"}>{s.published ? "Publik" : "Draf"}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-scenario-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(s.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Skenario Baru" : "Edit Skenario"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Judul</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="scenario-title-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Tingkat</Label><Input value={form.difficulty} onChange={(e) => upd("difficulty", e.target.value)} placeholder="Mudah/Sedang/Sulit" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Tujuan</Label><Textarea rows={2} value={form.objective} onChange={(e) => upd("objective", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Instruksi</Label><Textarea rows={2} value={form.instructions} onChange={(e) => upd("instructions", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Tantangan</Label><Input value={form.challenges} onChange={(e) => upd("challenges", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Pertanyaan (satu per baris)</Label><Textarea rows={2} value={Array.isArray(form.questions) ? form.questions.join("\n") : form.questions} onChange={(e) => upd("questions", e.target.value.split("\n"))} /></div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Parameter Awal (JSON)</Label><Textarea rows={8} className="font-data text-xs" value={presetText} onChange={(e) => setPresetText(e.target.value)} data-testid="scenario-preset-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Target Hasil (JSON)</Label><Textarea rows={8} className="font-data text-xs" value={targetText} onChange={(e) => setTargetText(e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => upd("published", v)} /><Label className="text-xs">Publikasikan</Label></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-scenario-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
